import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSortFilter, SortableTh, SearchBox } from "@/lib/tableUtils";
import { toast } from "sonner";
import { PageHeader, Card, Stat } from "@/components/UI";

const STAGES = [
  { key: "yarn", label: "YARN", unit: "kg" },
  { key: "knitting", label: "KNITTING", unit: "kg" },
  { key: "dyeing", label: "DYEING", unit: "kg" },
  { key: "compacting", label: "COMPACTING", unit: "kg" },
  { key: "printing", label: "PRINTING", unit: "kg" },
  { key: "cutting", label: "CUTTING", unit: "→ pcs" },
  { key: "stitching", label: "STITCHING", unit: "pcs" },
  { key: "packing", label: "PACKING", unit: "pcs" },
  { key: "dispatch", label: "DISPATCH", unit: "pcs" },
];

const TONE = {
  amber:   { chip: "bg-amber-100 text-amber-800",     dot: "bg-amber-500" },
  cyan:    { chip: "bg-cyan-100 text-cyan-800",       dot: "bg-cyan-500" },
  violet:  { chip: "bg-violet-100 text-violet-800",   dot: "bg-violet-500" },
  rose:    { chip: "bg-rose-100 text-rose-800",       dot: "bg-rose-500" },
  stone:   { chip: "bg-stone-100 text-stone-800",     dot: "bg-stone-500" },
  indigo:  { chip: "bg-indigo-100 text-indigo-800",   dot: "bg-indigo-500" },
  emerald: { chip: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
};

function WipCell({ testid, label, value, unit, tone = "amber", age = 0, onClick }) {
  const t = TONE[tone] || TONE.amber;
  const has = Number(value) > 0;
  const alarming = has && age >= 7;
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`p-4 md:p-5 ${clickable ? "cursor-pointer hover:bg-secondary/40 transition-colors" : ""}`}
      onClick={clickable ? onClick : undefined}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${has ? t.dot : "bg-muted"}`} />
        <span className="label-caps">{label}</span>
        {has && age > 0 && (
          <span
            data-testid={`${testid}-age`}
            className={`label-caps px-1.5 py-0.5 border ${
              alarming ? "bg-destructive/10 text-destructive border-destructive/40" : "border-border text-muted-foreground"
            }`}
          >
            oldest {age}d
          </span>
        )}
        {clickable && <span className="ml-auto label-caps text-muted-foreground">tap ›</span>}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display font-black text-3xl leading-none tabular tracking-tight">
          {fmtNum(value, unit === "Kg" ? 1 : 0)}
        </span>
        <span className={`label-caps px-1.5 py-0.5 ${t.chip}`}>{unit}</span>
      </div>
      <div className="mt-1 text-[10px] font-mono text-muted-foreground">
        {has ? "still with vendor" : "all cleared"}
      </div>
    </div>
  );
}

function PoolCell({ testid, label, value, tone = "stone", hint }) {
  const t = TONE[tone] || TONE.stone;
  const has = Number(value) > 0;
  return (
    <div className="p-4 md:p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${has ? t.dot : "bg-muted"}`} />
        <span className="label-caps">{label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display font-black text-3xl leading-none tabular tracking-tight">
          {fmtNum(value, 1)}
        </span>
        <span className={`label-caps px-1.5 py-0.5 ${t.chip}`}>Kg</span>
      </div>
      <div className="mt-1 text-[10px] font-mono text-muted-foreground">{hint}</div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [pool, setPool] = useState(null);
  const [yarnStock, setYarnStock] = useState(null);
  const [wipDrawer, setWipDrawer] = useState(null); // 'stitching' | 'knitting' | 'dyeing' | 'compacting' | 'printing' | null
  const [wipBreakdown, setWipBreakdown] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/ledger/summary").then((r) => setLedger(r.data));
    api.get("/stock").then((r) => setStock(r.data));
    api.get("/alerts").then((r) => setAlerts(r.data));
    api.get("/fabric-pool").then((r) => setPool(r.data)).catch(() => {});
    api.get("/yarn/stock-summary").then((r) => setYarnStock(r.data)).catch(() => {});
  }, []);

  const openStitchDrawer = async () => {
    try {
      const { data } = await api.get("/wip/stitching");
      setWipBreakdown(data);
      setWipDrawer("stitching");
    } catch { /* silent */ }
  };

  const openKdcDrawer = async (stage) => {
    try {
      const { data } = await api.get(`/wip/kdc/${stage}`);
      setWipBreakdown(data);
      setWipDrawer(stage);
    } catch { /* silent */ }
  };

  const sendDigest = async () => {
    if (!window.confirm("Send today's digest email now?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Digest sent to ${data.sent.length} recipient${data.sent.length !== 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const stageVal = (s) => {
    if (!d) return "—";
    if (s === "yarn") return fmtNum(d.yarn.kg, 1);
    if (["knitting", "dyeing", "compacting"].includes(s)) return fmtNum(d[s].kg_out, 1);
    if (s === "cutting") return fmtNum(d.cutting.pcs, 0);
    if (s === "stitching") return fmtNum(d.stitching.inhouse_pcs + d.stitching.outsourced_pcs, 0);
    if (s === "packing") return fmtNum(d.packing.pcs, 0);
    if (s === "dispatch") return fmtNum(d.dispatch.pcs, 0);
    return "—";
  };

  const totalStock = stock.reduce((a, b) => a + Math.max(0, b.stock_pcs || 0), 0);
  const totalStockBoxes = stock.reduce((a, b) => a + Math.max(0, b.stock_boxes || 0), 0);
  const totalPayable = ledger.reduce((a, b) => a + Math.max(0, b.balance || 0), 0);

  const { q: stockQ, setQ: setStockQ, sort: stockSort, toggleSort: toggleStockSort, rows: stockRows } = useSortFilter(stock, {
    searchKeys: ["style_name", "sku", "size", "color"],
  });

  return (
    <div>
      <PageHeader
        label="Overview"
        title="Production Command"
        subtitle="Kg → Pcs pipeline · Live from yarn to dispatch"
        right={user?.role === "admin" && (
          <button
            onClick={sendDigest}
            disabled={sending}
            data-testid="send-digest-now"
            className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-white border border-foreground hover:bg-foreground hover:text-background transition-colors disabled:opacity-50"
          >
            {sending ? "Sending…" : "✉ Send Digest Now"}
          </button>
        )}
      />

      {alerts && alerts.total > 0 && (
        <div className="mx-4 md:mx-8 mt-4 md:mt-6 border-l-4 border-destructive bg-destructive/5 p-4" data-testid="alerts-banner">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                <span className="label-caps text-destructive">Low Stock Alert · {alerts.total} item{alerts.total > 1 ? "s" : ""}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {alerts.skus.map((s) => (
                  <span key={s.id} className="text-xs font-mono bg-white border border-destructive/30 px-2 py-1">
                    {s.name} · <b>{s.stock}</b> ≤ {s.reorder_level}
                  </span>
                ))}
                {alerts.accessories.map((a) => (
                  <span key={a.id} className="text-xs font-mono bg-white border border-destructive/30 px-2 py-1">
                    {a.name} <span className="label-caps text-muted-foreground">{a.type}</span> · <b>{a.stock}</b> ≤ {a.reorder_level}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI ROW */}
      <div className="p-4 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Stat label="Yarn Purchased" value={fmtNum(d?.yarn?.kg || 0, 1)} unit="Kg" hint={`Value ${fmtINR(d?.yarn?.amount || 0)}`} />
        <Stat label="Finished Stock" value={fmtNum(totalStock, 0)} unit="Pcs" hint={`${fmtNum(totalStockBoxes, 0)} box · Packed − Dispatched`} />
        <Stat label="Dispatched" value={fmtNum(d?.dispatch?.pcs || 0, 0)} unit="Pcs" hint={`${fmtNum(d?.dispatch?.boxes || 0, 0)} box · ${fmtINR(d?.dispatch?.amount || 0)}`} />
        <Stat label="Payable to Parties" value={fmtINR(totalPayable)} unit="" hint={`${ledger.length} accounts`} />
      </div>

      {/* PIPELINE STRIP */}
      <div className="px-4 md:px-8">
        <div className="bg-white border border-border">
          <div className="border-b border-border px-4 py-2.5 label-caps">Pipeline (Kg → Pcs)</div>
          <div className="p-6 overflow-x-auto">
            <div className="flex items-stretch gap-6 min-w-max">
              {STAGES.map((s, i) => (
                <div key={s.key} className="pipe-step flex-1 min-w-[130px]">
                  <div className="label-caps">{s.label}</div>
                  <div className="font-display font-black text-2xl leading-none mt-2 tracking-tight">
                    {stageVal(s.key)}
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-muted-foreground">{s.unit}</div>
                  <div className="mt-3 h-[3px] bg-foreground w-8" />
                  <div className="mt-1 text-[10px] font-mono text-muted-foreground">STEP {i + 1}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* WIP · Work-in-Process still with vendors */}
      <div className="px-4 md:px-8 pt-4">
        <div className="bg-white border border-border" data-testid="wip-panel">
          <div className="border-b border-border px-4 py-2.5 label-caps flex items-center justify-between">
            <span>Vendor WIP · Out & Pending Return</span>
            <span className="text-[10px] font-mono text-muted-foreground normal-case tracking-normal">sent − received per stage</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
            <WipCell testid="wip-knitting"   label="Knitting"   value={d?.wip?.knitting_kg   ?? 0} age={d?.wip?.oldest?.knitting_days   ?? 0} unit="Kg"  tone="amber"  onClick={() => openKdcDrawer("knitting")} />
            <WipCell testid="wip-dyeing"     label="Dyeing"     value={d?.wip?.dyeing_kg     ?? 0} age={d?.wip?.oldest?.dyeing_days     ?? 0} unit="Kg"  tone="cyan"   onClick={() => openKdcDrawer("dyeing")} />
            <WipCell testid="wip-compacting" label="Compacting" value={d?.wip?.compacting_kg ?? 0} age={d?.wip?.oldest?.compacting_days ?? 0} unit="Kg"  tone="violet" onClick={() => openKdcDrawer("compacting")} />
            <WipCell testid="wip-printing"   label="Printing"   value={d?.wip?.printing_kg   ?? 0} age={d?.wip?.oldest?.printing_days   ?? 0} unit="Kg"  tone="emerald" onClick={() => openKdcDrawer("printing")} />
            <WipCell testid="wip-stitching"  label="Stitching"  value={d?.wip?.stitching_pcs ?? 0} age={d?.wip?.oldest?.stitching_days  ?? 0} unit="Pcs" tone="rose"   onClick={openStitchDrawer} />
          </div>
        </div>
      </div>

      {/* Fabric Pool · idle kilos ready for the next stage */}
      {pool && (
        <div className="px-4 md:px-8 pt-4" data-testid="dashboard-fabric-pool">
          <div className="bg-white border border-border">
            <div className="border-b border-border px-4 py-2.5 label-caps flex items-center justify-between">
              <span>Fabric Pool · Idle Kilos Ready for Next Stage</span>
              <span className="text-[10px] font-mono text-muted-foreground normal-case tracking-normal">received − consumed by next stage</span>
            </div>
            <div className="grid grid-cols-3 divide-x divide-border">
              <PoolCell testid="dash-pool-grey"     label="Grey Fabric"     value={pool.grey_kg}     tone="stone"   hint={`knit ${pool.totals.knitting_received} − dye-in ${pool.totals.dyeing_consumed}`} />
              <PoolCell testid="dash-pool-dyed"     label="Dyed Fabric"     value={pool.dyed_kg}     tone="indigo"  hint={`dye ${pool.totals.dyeing_received} − comp-in ${pool.totals.compacting_consumed}`} />
              <PoolCell testid="dash-pool-finished" label="Finished Fabric" value={pool.finished_kg} tone="emerald" hint={`comp ${pool.totals.compacting_received} − cut ${pool.totals.cutting_consumed}`} />
            </div>
          </div>
        </div>
      )}

      {/* Yarn Stock Summary · what's available before pricing new orders */}
      {yarnStock && (yarnStock.by_supplier?.length > 0 || yarnStock.by_type?.length > 0) && (
        <div className="px-4 md:px-8 pt-4" data-testid="dashboard-yarn-stock">
          <div className="bg-white border border-border">
            <div className="border-b border-border px-4 py-2.5 label-caps flex items-center justify-between flex-wrap gap-2">
              <span>Yarn Stock · Available before Pricing</span>
              <span className="text-[10px] font-mono text-muted-foreground normal-case tracking-normal">
                Purchased {fmtNum(yarnStock.total_purchased_kg, 1)} kg · With knitters {fmtNum(yarnStock.total_with_knitters_kg, 1)} kg · <b className="text-foreground">Available {fmtNum(yarnStock.total_available_kg, 1)} kg</b>
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
              <div className="p-3">
                <div className="label-caps mb-2">By Supplier</div>
                <div className="max-h-48 overflow-auto" data-testid="yarn-stock-suppliers">
                  <table className="table-dense w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr><th>Supplier</th><th className="text-right">Purchased Kg</th><th className="text-right">Available Kg</th></tr>
                    </thead>
                    <tbody>
                      {yarnStock.by_supplier.map((r) => (
                        <tr key={r.supplier_id || r.supplier_name}>
                          <td className="text-xs">{r.supplier_name}</td>
                          <td className="tabular text-right text-xs">{fmtNum(r.kg, 1)}</td>
                          <td className="tabular text-right font-bold text-xs">{fmtNum(r.avail, 1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-3">
                <div className="label-caps mb-2">By Yarn Type</div>
                <div className="max-h-48 overflow-auto" data-testid="yarn-stock-types">
                  <table className="table-dense w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr><th>Type</th><th className="text-right">Purchased Kg</th><th className="text-right">Available Kg</th></tr>
                    </thead>
                    <tbody>
                      {yarnStock.by_type.map((r) => (
                        <tr key={r.yarn_type}>
                          <td className="text-xs">{r.yarn_type}</td>
                          <td className="tabular text-right text-xs">{fmtNum(r.kg, 1)}</td>
                          <td className="tabular text-right font-bold text-xs">{fmtNum(r.avail, 1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECOND ROW */}
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <Card title="Stitching Mix" className="lg:col-span-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-caps">Inhouse</div>
              <div className="font-display font-black text-2xl mt-1">{fmtNum(d?.stitching?.inhouse_pcs || 0, 0)}</div>
              <div className="label-caps">pcs</div>
            </div>
            <div>
              <div className="label-caps">Outsourced</div>
              <div className="font-display font-black text-2xl mt-1">{fmtNum(d?.stitching?.outsourced_pcs || 0, 0)}</div>
              <div className="label-caps">pcs · {fmtINR(d?.stitching?.outsourced_cost || 0)}</div>
            </div>
          </div>
        </Card>

        <Card title="Fabric → Cut Conversion" className="lg:col-span-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-caps">Fabric Cut</div>
              <div className="font-display font-black text-2xl mt-1">{fmtNum(d?.cutting?.fabric_kg || 0, 1)}</div>
              <div className="label-caps">kg</div>
            </div>
            <div>
              <div className="label-caps">Pieces Out</div>
              <div className="font-display font-black text-2xl mt-1">{fmtNum(d?.cutting?.pcs || 0, 0)}</div>
              <div className="label-caps">pcs</div>
            </div>
          </div>
        </Card>

        <Card title="Top Payables" className="lg:col-span-1">
          <div className="space-y-2">
            {ledger
              .filter((l) => (l.balance || 0) > 0.01)
              .sort((a, b) => b.balance - a.balance)
              .slice(0, 4)
              .map((l) => (
                <div key={l.party_id} className="flex justify-between border-b border-border py-1.5">
                  <div className="text-xs">{l.name} <span className="label-caps ml-1">{l.type}</span></div>
                  <div className="tabular text-sm font-semibold">{fmtINR(l.balance)}</div>
                </div>
              ))}
            {ledger.filter((l) => (l.balance || 0) > 0.01).length === 0 && (
              <div className="text-xs text-muted-foreground font-mono py-6 text-center">No dues</div>
            )}
          </div>
        </Card>
      </div>

      {/* STOCK TABLE */}
      <div className="px-4 md:px-8 pb-8 md:pb-12">
        <Card title={`Finished Stock by SKU · ${stockRows.length}${stockRows.length !== stock.length ? ` of ${stock.length}` : ""}`} right={
          <SearchBox value={stockQ} onChange={setStockQ} placeholder="Search style, SKU, size, color…" testid="dashboard-stock-search" />
        }>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="table-dense w-full">
              <thead className="sticky top-0 z-10">
                <tr>
                  <SortableTh label="Style" sortKey="style_name" sort={stockSort} toggleSort={toggleStockSort} />
                  <SortableTh label="SKU" sortKey="sku" sort={stockSort} toggleSort={toggleStockSort} />
                  <SortableTh label="Size" sortKey="size" sort={stockSort} toggleSort={toggleStockSort} />
                  <SortableTh label="Color" sortKey="color" sort={stockSort} toggleSort={toggleStockSort} />
                  <SortableTh label="Pcs/Box" sortKey="pieces_per_box" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Cut" sortKey="cut_pcs" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Stitched" sortKey="stitched_pcs" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Packed Pcs" sortKey="packed_pcs" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Packed Box" sortKey="packed_boxes" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Disp Pcs" sortKey="dispatched_pcs" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Disp Box" sortKey="dispatched_boxes" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Stock Pcs" sortKey="stock_pcs" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                  <SortableTh label="Stock Box" sortKey="stock_boxes" sort={stockSort} toggleSort={toggleStockSort} className="text-right" />
                </tr>
              </thead>
              <tbody>
                {stockRows.map((s) => (
                  <tr key={s.id} data-testid={`stock-row-${s.sku}`}>
                    <td>{s.style_name}</td>
                    <td className="font-mono">{s.sku}</td>
                    <td>{s.size}</td>
                    <td>{s.color}</td>
                    <td className="tabular text-right text-muted-foreground">{s.pieces_per_box || "—"}</td>
                    <td className="tabular text-right">{s.cut_pcs}</td>
                    <td className="tabular text-right">{s.stitched_pcs}</td>
                    <td className="tabular text-right">{s.packed_pcs}</td>
                    <td className="tabular text-right">{s.packed_boxes || 0}</td>
                    <td className="tabular text-right">{s.dispatched_pcs}</td>
                    <td className="tabular text-right">{s.dispatched_boxes || 0}</td>
                    <td className="tabular text-right font-bold">{s.stock_pcs}</td>
                    <td className={`tabular text-right font-bold ${(s.stock_boxes || 0) > 0 ? "" : "text-muted-foreground"}`}>{s.stock_boxes || 0}</td>
                  </tr>
                ))}
                {stockRows.length === 0 && (
                  <tr><td colSpan={13} className="text-center text-muted-foreground py-8 font-mono">{stock.length ? "No SKUs match your search" : "No products yet. Add products first →"}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* WIP breakdown drawer — works for stitching (pcs) and KDC(P) stages (kg) */}
      {wipDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="wip-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setWipDrawer(null)} />
          <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl border-l border-border animate-in slide-in-from-right">
            {(() => {
              const isStitch = wipDrawer === "stitching";
              const unit = isStitch ? "Pcs" : "Kg";
              const total = isStitch ? (d?.wip?.stitching_pcs ?? 0) : (d?.wip?.[`${wipDrawer}_kg`] ?? 0);
              const label = wipDrawer.charAt(0).toUpperCase() + wipDrawer.slice(1);
              return (
                <>
                  <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-start justify-between">
                    <div>
                      <div className="label-caps text-muted-foreground">Vendor WIP · Breakdown</div>
                      <div className="font-display text-2xl font-black mt-1">{label} · {fmtNum(total, isStitch ? 0 : 1)} {unit}</div>
                      <div className="text-xs text-muted-foreground mt-1">Grouped by party · oldest first</div>
                    </div>
                    <button onClick={() => setWipDrawer(null)} data-testid="wip-drawer-close" className="label-caps hover:text-destructive">Close ✕</button>
                  </div>
                  <div className="p-5 space-y-4">
                    {wipBreakdown.length === 0 && (
                      <div className="text-sm text-muted-foreground py-8 text-center">All clear — no pending {unit.toLowerCase()}.</div>
                    )}
                    {wipBreakdown.map((p) => {
                      const alarming = p.oldest_days >= 7;
                      const rowTotal = isStitch ? p.total_pcs : p.total_kg;
                      const src1 = isStitch ? p.jobs_pcs : p.batch_kg;
                      const src2 = isStitch ? p.dc_pcs : p.dc_kg;
                      const src1Label = isStitch ? "Jobs" : "Batch";
                      return (
                        <div key={p.party_id} data-testid={`wip-party-${p.party_id}`} className="border border-border bg-white">
                          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                            <div>
                              <div className="font-black text-lg">{p.party_name}</div>
                              <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                                {src1Label} {src1} · DC {src2}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-display text-2xl font-black tabular">{rowTotal}</div>
                              <div className={`label-caps mt-1 inline-block px-1.5 py-0.5 border ${alarming ? "bg-destructive/10 text-destructive border-destructive/40" : "border-border text-muted-foreground"}`}>oldest {p.oldest_days}d</div>
                            </div>
                          </div>
                          <table className="table-dense w-full text-xs">
                            <thead>
                              <tr>
                                <th>Source</th>
                                {isStitch && <th>Product</th>}
                                <th>Date</th>
                                <th className="text-right">{unit}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.sources.map((s, i) => (
                                <tr key={s.id + "-" + i}>
                                  <td>
                                    <span className={`label-caps px-1.5 py-0.5 ${s.type === "dc" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>{s.type === "dc" ? "DC" : (isStitch ? "Job" : "Batch")}</span>
                                    <span className="ml-2 font-mono text-[11px]">{s.ref}</span>
                                  </td>
                                  {isStitch && <td>{s.product_label || <span className="text-muted-foreground">—</span>}</td>}
                                  <td className="font-mono">{s.date}</td>
                                  <td className="tabular text-right font-bold">{isStitch ? s.pcs : s.kg}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
